import { Button, Card, makeStyles, Text, tokens } from '@fluentui/react-components';
import { Dismiss20Regular, PersonSupport20Regular } from '@fluentui/react-icons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { ConflictError } from '../../../libs/services/BaseService';
import { CustomerLiveChatService, EscalateConflictReasons } from '../../../libs/services/CustomerLiveChatService';
import { useAppDispatch, useAppSelector } from '../../../redux/app/hooks';
import { RootState } from '../../../redux/app/store';
import { dismissEscalationOffer } from '../../../redux/features/livechat/escalationOfferSlice';

const useStyles = makeStyles({
    card: {
        backgroundColor: tokens.colorNeutralBackground2,
        borderLeft: `4px solid ${tokens.colorBrandStroke1}`,
        padding: tokens.spacingVerticalM,
        marginTop: tokens.spacingVerticalS,
        position: 'relative',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: tokens.spacingVerticalS,
    },
    title: {
        fontWeight: tokens.fontWeightSemibold,
    },
    body: {
        display: 'block',
        marginBottom: tokens.spacingVerticalM,
        color: tokens.colorNeutralForeground2,
    },
    status: {
        display: 'block',
        color: tokens.colorNeutralForeground2,
    },
    error: {
        color: tokens.colorPaletteRedForeground1,
    },
    dismiss: {
        minWidth: '24px',
        height: '24px',
        padding: 0,
    },
});

interface InlineEscalationOfferProps {
    chatId: string;
}

type State =
    | { kind: 'idle' }
    | { kind: 'pending' }
    | { kind: 'queued'; expectedWaitSeconds: number | null }
    | { kind: 'error'; message: string };

export const InlineEscalationOffer: React.FC<InlineEscalationOfferProps> = ({ chatId }) => {
    const styles = useStyles();
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const auth = useAuth();
    const token = auth.user?.access_token;
    const hostContext = useAppSelector((state: RootState) => state.hostContext.hostContext);
    const [state, setState] = useState<State>({ kind: 'idle' });

    const formatExpectedWait = (seconds: number) => {
        const minutes = Math.round(seconds / 60);
        return minutes < 1
            ? t('escalationOffer.expectedWaitUnderMinute')
            : t('escalationOffer.expectedWait', { count: minutes });
    };

    if (!token) {
        return null;
    }

    const accept = async () => {
        setState({ kind: 'pending' });
        try {
            const service = new CustomerLiveChatService(token);
            const response = await service.escalateAsync(chatId, hostContext);
            setState({ kind: 'queued', expectedWaitSeconds: response.expectedWaitSeconds ?? null });
        } catch (err) {
            const reason = err instanceof ConflictError ? err.reason : undefined;
            // Only already_queued/already_handled mean a human is genuinely coming → show the queued
            // state. no_agents_online and the transient 409s (queue_full, concurrently_modified) queued
            // nothing, so surface an error rather than imply someone is on the way.
            if (reason === EscalateConflictReasons.AlreadyQueued || reason === EscalateConflictReasons.AlreadyHandled) {
                setState({ kind: 'queued', expectedWaitSeconds: null });
            } else if (reason === EscalateConflictReasons.NoAgentsOnline) {
                setState({ kind: 'error', message: t('escalationOffer.errorNoAgents') });
            } else {
                setState({ kind: 'error', message: t('escalationOffer.errorGeneric') });
            }
        }
    };

    const dismiss = async () => {
        // Hide immediately for a responsive feel; persist so it stays hidden across reloads.
        dispatch(dismissEscalationOffer({ chatId }));
        try {
            const service = new CustomerLiveChatService(token);
            await service.dismissOfferAsync(chatId, hostContext);
        } catch (err) {
            // Best-effort: the local hide already happened. A failed persist just means the offer
            // may reappear on a future reload — harmless, and the once-per-chat gate still holds.
            console.error('LiveChat: failed to persist escalation offer dismissal', err);
        }
    };

    if (state.kind === 'queued') {
        return (
            <Card className={styles.card} aria-live="polite" data-testid="inline-escalation-offer">
                <Text className={styles.status}>{t('escalationOffer.waiting')}</Text>
                {state.expectedWaitSeconds !== null && (
                    <Text className={styles.status} data-testid="inline-escalation-offer-expected-wait">
                        {formatExpectedWait(state.expectedWaitSeconds)}
                    </Text>
                )}
            </Card>
        );
    }

    return (
        <Card className={styles.card} data-testid="inline-escalation-offer">
            <div className={styles.header}>
                <Text className={styles.title}>{t('escalationOffer.title')}</Text>
                <Button
                    appearance="transparent"
                    icon={<Dismiss20Regular />}
                    className={styles.dismiss}
                    aria-label={t('escalationOffer.dismiss')}
                    onClick={() => {
                        void dismiss();
                    }}
                    data-testid="inline-escalation-offer-dismiss"
                />
            </div>
            <Text className={styles.body}>{t('escalationOffer.body')}</Text>
            <Button
                appearance="primary"
                icon={<PersonSupport20Regular />}
                disabled={state.kind === 'pending'}
                onClick={() => {
                    void accept();
                }}
                data-testid="inline-escalation-offer-accept"
            >
                {t('escalationOffer.accept')}
            </Button>
            {state.kind === 'error' && <Text className={styles.error}>{state.message}</Text>}
        </Card>
    );
};
